import { decodeLatLngBounds } from './urlHelpers';

/**
 * Swiss city coworking landing pages: URL slug → bounding box for listing search.
 * Bounds format: ne_lat,ne_lng,sw_lat,sw_lng (see decodeLatLngBounds).
 *
 * The bounds cover the wider agglomeration / commuting area around each city,
 * not just the city core. This matches user expectations for "Coworking in <city>"
 * (most coworkers are happy with a space 10–15 km away).
 */
export const CITY_LANDING_SLUGS = [
  'zurich',
  'bern',
  'basel',
  'geneva',
  'lausanne',
  'lucerne',
  'st-gallen',
  'winterthur',
  'zug',
];

/**
 * Below this many listings a city page asks not to be indexed (`noindex, follow`) and the sitemap
 * leaves it out. Counted like the page counts: the city box, lounges and anything outside the
 * three space types (virtual office, coliving, event space) excluded — what the grid can show.
 * Two cards under a city headline are the soft-404 shape Search Console already reported once.
 * The server keeps a copy (server/api-util/seoCatalog.js); a parity test holds them together.
 */
export const MIN_INDEXABLE_CITY_LISTINGS = 3;

/**
 * Below this many listings of its type a city×type page (/coworking/:citySlug/:typSlug) asks not
 * to be indexed. Same threshold as the SEO plan's thin-content rule for category pages.
 */
export const MIN_INDEXABLE_TYPE_LISTINGS = 8;

/**
 * Path segment of a city×type page → space-type group key (components/CategoryShortcuts/
 * categoryGroups.js). The same ASCII words the `?typ=` ad deep link carries, so an ad group
 * "Meetingraum" and the indexable page share one vocabulary. The umlaut spelling `büro` is
 * deliberately not a path: one URL per page.
 */
export const CITY_TYPE_SLUGS = Object.freeze({
  arbeitsplatz: 'desk',
  meetingraum: 'meeting',
  buero: 'office',
});

const hasOwn = (object, key) =>
  typeof key === 'string' && Object.prototype.hasOwnProperty.call(object, key);

/**
 * @param {string} typSlug path segment, e.g. `meetingraum`
 * @returns {'desk'|'meeting'|'office'|null} null for anything that is not exactly one of the
 *   three words (case-sensitive: `/coworking/zurich/Meetingraum` is a 404, not a second URL)
 */
export const cityTypeSlugToGroup = typSlug =>
  hasOwn(CITY_TYPE_SLUGS, typSlug) ? CITY_TYPE_SLUGS[typSlug] : null;

/**
 * @param {string} groupKey `desk` | `meeting` | `office`
 * @returns {string|null} the path segment for that type
 */
export const cityTypeGroupToSlug = groupKey =>
  Object.keys(CITY_TYPE_SLUGS).find(slug => CITY_TYPE_SLUGS[slug] === groupKey) || null;

/**
 * Open Graph preview (Unsplash; allow in CSP imgSrc). Motifs aligned with SectionLocations where applicable.
 *
 * `center` is the main railway station: the city page ranks its listings by distance from here
 * (see containers/CityLandingPage/citySelection.js). The boxes above deliberately reach into the
 * neighbouring towns, and without a centre the newest import — lately Winterthur — led the Zurich
 * page. `airportCode` marks the cities whose box contains an airport with lounges on the
 * marketplace; the city page then cross-links the lounge hub instead of mixing lounges into the
 * coworking grid.
 *
 * `wikidata` names the city entity for the WebPage's `about` (schema.org City + sameAs), so search
 * engines do not have to guess which "Zug" or "Bern" the page means. Each id was checked against
 * Special:EntityData (label, "Stadt in der Schweiz", coordinates) on 2026-10-09 — Q11943, the id
 * an audit proposed for Zug, is the Canton of Zurich; Zug is Q68144.
 *
 * `indexableTypes` lists the city×type pages (/coworking/:citySlug/:typSlug, words from
 * CITY_TYPE_SLUGS) that are meant to rank on their own. Static on purpose: the sitemap is built on
 * the server without the listing pool, and a page only joins this list once the inventory clears
 * MIN_INDEXABLE_TYPE_LISTINGS with room to spare (prod 2026-10-08: Zürich 35 desks / 23 meeting
 * rooms / 10 offices, Winterthur 10 meeting rooms). A type page outside this list still renders,
 * but points its canonical at the city and asks not to be indexed. The server copy lives in
 * server/api-util/seoCatalog.js.
 */
export const CITY_LANDING_CONFIG = {
  zurich: {
    // Bülach (N) – Pfäffikon ZH (E) – Affoltern a.A. (S) – Limmattal/Dietikon (W)
    boundsStr: '47.510,8.745,47.260,8.340',
    center: { lat: 47.3779, lng: 8.5403 }, // Zürich HB
    wikidata: 'Q72',
    indexableTypes: ['arbeitsplatz', 'meetingraum', 'buero'],
    airportCode: 'ZRH',
    ogImageUrl:
      'https://images.unsplash.com/photo-1554899199-f6d99e6be6f7?w=1200&h=630&fit=crop&crop=center',
  },
  bern: {
    // Schönbühl/Zollikofen (N) – Worb (E) – Belp/Kehrsatz (S) – Mühleberg (W)
    boundsStr: '47.050,7.620,46.870,7.290',
    center: { lat: 46.9489, lng: 7.4394 }, // Bahnhof Bern
    wikidata: 'Q70',
    ogImageUrl:
      'https://images.unsplash.com/photo-1749588292359-9236987cfab3?w=1200&h=630&fit=crop&crop=center',
  },
  basel: {
    // FR border (N) – Pratteln/Liestal (E) – Reinach/Aesch (S) – Saint-Louis (W)
    boundsStr: '47.665,7.770,47.460,7.480',
    center: { lat: 47.5476, lng: 7.5897 }, // Basel SBB
    wikidata: 'Q78',
    ogImageUrl:
      'https://images.unsplash.com/photo-1707321519218-f0a61ce4a249?w=1200&h=630&fit=crop&crop=center',
  },
  geneva: {
    // Versoix (N) – Veyrier (E) – Plan-les-Ouates (S) – Meyrin/Vernier (W)
    boundsStr: '46.305,6.290,46.130,5.970',
    center: { lat: 46.2102, lng: 6.1425 }, // Genève-Cornavin
    wikidata: 'Q71',
    airportCode: 'GVA',
    ogImageUrl:
      'https://images.unsplash.com/photo-1752346168893-99056677e672?w=1200&h=630&fit=crop&crop=center',
  },
  lausanne: {
    // Lavaux (E) – Pully south – Morges (W) – Renens
    boundsStr: '46.610,6.770,46.450,6.480',
    center: { lat: 46.5169, lng: 6.6291 }, // Gare de Lausanne
    wikidata: 'Q807',
    ogImageUrl:
      'https://images.unsplash.com/photo-1650057915898-6761696fddf8?w=1200&h=630&fit=crop&crop=center',
  },
  lucerne: {
    // Emmen/Rothenburg (N) – Küssnacht/Vitznau (E) – Horw/Kriens (S) – Sursee dir. (W)
    boundsStr: '47.145,8.430,46.940,8.150',
    center: { lat: 47.0502, lng: 8.3102 }, // Bahnhof Luzern
    wikidata: 'Q4191',
    ogImageUrl:
      'https://images.unsplash.com/photo-1750845372022-f4c633f433d5?w=1200&h=630&fit=crop&crop=center',
  },
  'st-gallen': {
    // Lake Constance (N) – Goldach (E) – Herisau (S) – Gossau (W)
    boundsStr: '47.510,9.510,47.360,9.260',
    center: { lat: 47.4232, lng: 9.3697 }, // Bahnhof St. Gallen
    wikidata: 'Q25607',
    ogImageUrl:
      'https://images.unsplash.com/photo-1712839398660-2642f9e21547?w=1200&h=630&fit=crop&crop=center',
  },
  winterthur: {
    // Seuzach (N) – Wiesendangen (E) – Effretikon (S) – Pfungen (W)
    boundsStr: '47.580,8.880,47.420,8.620',
    center: { lat: 47.5001, lng: 8.7241 }, // Bahnhof Winterthur
    wikidata: 'Q9125',
    indexableTypes: ['meetingraum'],
    ogImageUrl:
      'https://images.unsplash.com/photo-1683727610281-26da8c641e4e?w=1200&h=630&fit=crop&crop=center',
  },
  zug: {
    // Cham/Hünenberg (W) – Baar/Steinhausen (N) – Walchwil (S)
    boundsStr: '47.240,8.625,47.090,8.420',
    center: { lat: 47.1734, lng: 8.515 }, // Bahnhof Zug
    wikidata: 'Q68144',
    ogImageUrl:
      'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=630&fit=crop&crop=center',
  },
};

/**
 * @param {string} slug URL segment
 * @returns {{ slug: string, boundsStr: string, center?: {lat: number, lng: number}, airportCode?: string, wikidata?: string, indexableTypes?: string[], ogImageUrl?: string } | null}
 */
export const getCityLandingConfig = slug => {
  // Own keys only: the slug is untrusted URL input, and `/coworking/constructor` used to resolve
  // to Object.prototype.constructor and render a city page without bounds.
  if (!slug || !hasOwn(CITY_LANDING_CONFIG, slug)) {
    return null;
  }
  return { slug, ...CITY_LANDING_CONFIG[slug] };
};

/**
 * Whether the city×type page is one that should rank on its own (see `indexableTypes`). The
 * listing-count threshold (MIN_INDEXABLE_TYPE_LISTINGS) is checked by the page on top of this.
 *
 * @param {string} citySlug
 * @param {string} typSlug path segment from CITY_TYPE_SLUGS
 * @returns {boolean}
 */
export const isIndexableCityType = (citySlug, typSlug) => {
  const city = hasOwn(CITY_LANDING_CONFIG, citySlug) ? CITY_LANDING_CONFIG[citySlug] : null;
  return !!(city && cityTypeSlugToGroup(typSlug) && (city.indexableTypes || []).includes(typSlug));
};

/**
 * @param {string} id Wikidata item id, e.g. `Q72`
 * @returns {string|null} the entity URL schema.org `sameAs` expects
 */
export const wikidataUrl = id => (id ? `https://www.wikidata.org/wiki/${id}` : null);

/**
 * @param {string} boundsStr
 * @returns {object|null} SDK LatLngBounds or null
 */
export const getBoundsForSdk = boundsStr => decodeLatLngBounds(boundsStr);

/**
 * Which city landing page covers this point, if any.
 *
 * Exists so a listing can link back to its city. Until now that link ran one way only: city pages
 * pointed at listings, listings pointed nowhere, and the nine pages meant to rank for "coworking
 * <city>" got no internal links from the ~85 pages that actually carry content.
 *
 * The boxes overlap around the agglomerations (Zurich's reaches Dietikon, Winterthur's reaches
 * back toward it), so the SMALLEST matching box wins — the more specific page is the better
 * destination, and the result is stable rather than dependent on declaration order.
 *
 * @param {{lat: number, lng: number}} geolocation listing geolocation
 * @returns {string|null} city slug, or null when the point is outside every box
 */
// Parsed once at module load. The boxes are static, while the lookup runs on every render of
// every listing page — re-splitting nine strings and coercing thirty-six numbers each time was
// work with a constant answer.
const CITY_BOXES = CITY_LANDING_SLUGS.map(slug => {
  const parts = (CITY_LANDING_CONFIG[slug]?.boundsStr || '').split(',').map(Number);
  if (parts.length !== 4 || parts.some(Number.isNaN)) {
    return null;
  }
  const [neLat, neLng, swLat, swLng] = parts;
  return {
    slug,
    neLat,
    neLng,
    swLat,
    swLng,
    area: Math.abs(neLat - swLat) * Math.abs(neLng - swLng),
  };
}).filter(Boolean);

export const findCityLandingSlug = geolocation => {
  const lat = geolocation?.lat;
  const lng = geolocation?.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return null;
  }

  const match = CITY_BOXES.reduce((best, box) => {
    const contains = lat <= box.neLat && lat >= box.swLat && lng <= box.neLng && lng >= box.swLng;
    if (!contains) {
      return best;
    }
    return !best || box.area < best.area ? box : best;
  }, null);

  // null, never undefined: that is the documented contract, and callers branch on it.
  return match ? match.slug : null;
};
